// Helpers
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { get as getProperty, isEmpty, isEqual, trim } from "lodash";

// Utils
const isAllowedOperator = (operator) => [">", ">=", "<", "<=", "!=", "==", "===", "*"].includes(operator);

const getDeltas = (deltas = null) => deltas || { Unit: 0.5, Dollar: 0.01, Percent: 0.01, Duration: 0 };

const getDecimalPlaces = (number = 0) => {
	if (number) {
		return number.toString().split(".")?.[1]?.length || 0;
	}
	return 0;
};

const toFloatValue = (value, increment = 0, decimals = 0) => {
	value = Boolean(value) ? value : 0;
	if (isNumeric(value)) {
		let result = parseFloat(value);
		if (increment) {
			result += parseFloat(increment);
		}
		if (decimals) {
			result = result.toFixed(decimals);
		}
		return parseFloat(result);
	}
	return value;
};

const mapMatrixSection = (section = [], mapKeys = [], mapOn = "") => {
	const result = [];
	const sectionToMap = mapSection(section, [mapOn]);
	if (sectionToMap.length) {
		for (let i = 0; i < sectionToMap.length; i++) {
			const current = sectionToMap[i];
			if (mapOn in current) {
				result[i] = mapSection(current[mapOn], mapKeys);
			}
		}
	}
	return result;
};

const mapSection = (section = [], mapKeys = [], mapOn = null) => {
	if (section?.length && mapKeys?.length) {
		if (mapOn) {
			return mapMatrixSection(section, mapKeys, mapOn);
		}

		return section.map((item) => {
			return mapKeys.reduce((object, key) => {
				object[key] = item?.[key];
				return object;
			}, {});
		});
	}
	return section;
};

const reduceObjectToFalse = (element) => {
	return Object.keys(element).reduce((error, key) => {
		error[key] = false;
		let isArray = Array.isArray(element?.[key]);
		if (isArray) {
			error[key] = Array.from(element[key], () => false);
		}
		return error;
	}, {});
};

const getMatrixMapErrors = (section = []) => {
	return section.map((item) => {
		return [...item].reduce((error, element) => {
			const reduced = reduceObjectToFalse(element);
			return [...error, reduced];
		}, []);
	});
};

const getMapErrors = (section = []) => section.map((item) => reduceObjectToFalse(item));

const getValueOnProperty = (property = "", object = null) => {
	if (property && object) {
		return getProperty(object, property, null);
	}
	return object;
};

const writeErrorsOnIndex = (errors = [], index = 0) => {
	if (!errors.length) {
		return errors;
	}
	errors[index] = Object.keys(errors[index]).reduce((error, key) => {
		error[key] = true;
		if (Array.isArray(errors[index]?.[key])) {
			error[key] = Array.from(errors[index][key], () => true);
		}
		return error;
	}, {});
	return errors;
};

const getRowAsList = (row = null, mapKeys = [], index = 0) => {
	if (!row || !mapKeys?.length) {
		return [];
	}
	const result = [];
	for (const key of mapKeys) {
		const element = row?.[key] || null;
		result.push({
			index,
			element,
			on: key,
		});
	}
	return result;
};

const compare = (operator, first, second) => {
	switch (operator) {
		case ">":
			return first > second;
		case ">=":
			return first >= second;
		case "<":
			return first < second;
		case "<=":
			return first <= second;
		case "!=":
			return first != second;
		case "==":
			return first == second;
		case "===":
			return first === second;
		case "*":
		default:
			return true;
	}
};

// Keys utils
const getFromToKeys = (mapKeys = []) => {
	const from = mapKeys?.[0] || "from";
	const to = mapKeys?.[1] || "to";
	return { from, to };
};

const getSimpleIndex = (mapKeys = []) => mapKeys?.[0] || "commission";

const writeErrorCloseToValue = (errors, section, args) => {
	if (!section?.length) {
		return errors || [];
	}

	const { on = "from", closeTo = 0 } = args || {};

	let useIndex = -1;
	let differenceValue = 1000000000;
	for (let index = 0; index < section.length; index++) {
		const current = section[index]?.[on];
		if (current) {
			const difference = current.value - closeTo;
			if (!difference) {
				useIndex = index;
				break;
			}

			const min = Math.min(Math.abs(difference), differenceValue);
			if (min < differenceValue) {
				differenceValue = min;
				useIndex = index;
			}
		}
	}

	if (errors?.[useIndex]) {
		errors[useIndex]["from"] = true;
	}
	if (errors?.[useIndex - 1]) {
		errors[useIndex - 1]["to"] = true;
	}

	return errors;
};

// Special Checks
/**
 * noFirstError 		-> Remove the first error from the list
 * noLastError 			-> Remove the last error from the list
 * atLeastOneValid 		-> Remove all errors if at least one condition was valid
 * */
const checkSpecialProperties = (errors = [], section = [], mapKeys = [], type = "", checks = null) => {
	if (checks && errors.length) {
		switch (type) {
			case "DOUBLE_INPUT":
				{
					const { from, to } = getFromToKeys(mapKeys);
					const zeroes = haveZeroes(section, { from, to }, "DOUBLE_INPUT");

					if (checks?.oneStepZeroes && zeroes !== 1) {
						errors = writeErrorCloseToValue(errors, section, { on: "from" });
					}

					if (checks?.noFirstError) {
						errors[0][from] = false;
					}
					if (checks?.noLastError) {
						errors[errors.length - 1][to] = false;
					}
				}
				break;
			case "SIMPLE":
				{
					const index = getSimpleIndex(mapKeys);
					if (checks?.noFirstError) {
						errors[0][index] = false;
					}
					if (checks?.noLastError) {
						errors[errors.length - 1][index] = false;
					}
				}
				break;
			case "MULTIPLE":
				if (checks?.atLeastOneValid && isValidAtLeastOneValue(errors)) {
					return [];
				}
				break;
			default:
				break;
		}
	}
	return errors;
};

const evaluateCondition = (condition = null, modifiers = null, first = null, second = null) => {
	let evaluation = true;
	if (condition) {
		const on = condition?.on || null;
		const constraints = condition?.constraints || null;

		// General Modifier
		const useModifier = condition?.useModifier || null;
		let resolveFn = null;
		if (useModifier?.["function"]) {
			resolveFn = modifiers?.[useModifier.function] || null;
		}

		switch (condition.type) {
			case "HIERARCHICAL_UNIQUE": {
				if (!condition?.hierarchy || !Array.isArray(condition.hierarchy)) {
					console.error("For HIERARCHICAL_UNIQUE conditions you must provide a 'hierarchy' array");
					break;
				}

				const hierarchy = condition.hierarchy;

				for (let level = 0; level < hierarchy.length; level++) {
					const field = hierarchy[level];
					let firstValue = getValueOnProperty(field, first);
					let secondValue = getValueOnProperty(field, second);

					if (typeof firstValue === "string") {
						firstValue = isEmpty(firstValue) ? null : trim(firstValue);
					}
					if (typeof secondValue === "string") {
						secondValue = isEmpty(secondValue) ? null : trim(secondValue);
					}

					if (!isEqual(firstValue, secondValue)) {
						break;
					}

					if (level === hierarchy.length - 1) {
						evaluation = false;
					}
				}

				break;
			}
			case "SIMILAR": {
				if (!on) {
					console.error("For SIMILAR conditions you must provide an 'on' property");
					break;
				}

				// Basic evaluation condition
				const firstValue = getValueOnProperty(on, first);
				const secondValue = getValueOnProperty(on, second);

				evaluation = Boolean(firstValue === secondValue);

				// Resolvers
				if (resolveFn) {
					evaluation = resolveFn(firstValue, secondValue);
				}

				// Last check conditions
				if ("empty" in condition) {
					if (!firstValue || !secondValue) evaluation = Boolean(condition.empty);
				}

				break;
			}
			case "INTERSECTION": {
				if (on?.length < 2) {
					console.error("For INTERSECTION conditions you must provide a list of 'on' properties");
					break;
				}

				const applyIfCondition = condition?.applyIf;
				if (!!applyIfCondition) {
					evaluation = evaluateCondition(applyIfCondition, modifiers, first, second);
					if (!evaluation) {
						break;
					}
				}

				const firstRangeValues = [];
				const secondRangeValues = [];

				for (const definition of on) {
					const useLocalModifier = definition?.useModifier || null;
					let localResolveFn = null;
					let localWhen = null;

					if (useLocalModifier?.["function"]) {
						localResolveFn = modifiers?.[useLocalModifier.function] || null;
					}
					if (useLocalModifier?.["when"]) {
						localWhen = useLocalModifier.when;
					}

					let addFirst = getValueOnProperty(definition.key, first);
					let addSecond = getValueOnProperty(definition.key, second);

					if (localResolveFn) {
						const useWhen = {
							first: false,
							second: false,
						};

						if (localWhen) {
							useWhen.first = Boolean(getValueOnProperty(localWhen?.key, first) === localWhen?.value);
							useWhen.second = Boolean(getValueOnProperty(localWhen?.key, second) === localWhen?.value);
						}

						addFirst = localResolveFn(addFirst, useWhen.first);
						addSecond = localResolveFn(addSecond, useWhen.second);
					}

					firstRangeValues.push(addFirst);
					secondRangeValues.push(addSecond);
				}

				const variables = [...firstRangeValues, ...secondRangeValues];

				evaluation = resolveFn?.(...variables) || false;

				// Last check conditions
				if ("empty" in condition && variables.some((value) => !value)) {
					evaluation = Boolean(condition.empty);
				}

				break;
			}
			case "INLINE": {
				let deltas = null;
				if ("deltas" in condition) {
					deltas = getDeltas(condition.deltas);
				}

				let delta = 0;
				if (deltas) {
					const input = first?.["input"];
					delta = deltas?.[input] || 0;
				}

				const decimalPlaces = getDecimalPlaces(delta);

				if (condition?.compare && !isAllowedOperator(condition?.compare)) {
					console.error(`Invalid compare operator :::"${condition?.compare}"::: at evaluateCondition on handleValidation errors`);
					break;
				}

				if (!on) {
					console.error("For INLINE conditions you must provide an 'on' property");
					break;
				}

				// Basic evaluation condition
				const firstValue = getValueOnProperty(on, first);
				const secondValue = getValueOnProperty(on, second);

				const useFirstValue = toFloatValue(firstValue, delta, decimalPlaces);
				const useSecondValue = toFloatValue(secondValue, 0, decimalPlaces);

				evaluation = compare(condition.compare, useFirstValue, useSecondValue);

				if (evaluation && constraints) {
					const validConstraints = isValidConstraints(constraints, firstValue, secondValue);
					evaluation = Boolean(validConstraints);
				}
				break;
			}
			case "STEP": {
				let deltas = null;
				if ("deltas" in condition) {
					deltas = getDeltas(condition.deltas);
				}

				let delta = 0;
				if (deltas) {
					const input = first?.["input"];
					delta = deltas?.[input] || 0;
				}

				const decimalPlaces = getDecimalPlaces(delta);

				if (condition?.compare && !isAllowedOperator(condition?.compare)) {
					console.error(`Invalid compare operator :::"${condition?.compare}"::: at evaluateCondition on handleValidation errors`);
					break;
				}

				if (!on) {
					console.error("For STEP conditions you must provide an 'on' property");
					break;
				}

				// Basic evaluation condition
				// In STEP conditions values are inverted on purpose
				const firstValue = getValueOnProperty(on, second);
				const secondValue = getValueOnProperty(on, first);

				const useFirstValue = toFloatValue(firstValue, delta, decimalPlaces);
				const useSecondValue = toFloatValue(secondValue, 0, decimalPlaces);

				evaluation = compare(condition.compare, useFirstValue, useSecondValue);

				if ("distinct" in condition) {
					evaluation = compare("!=", useFirstValue, condition.distinct) && compare("!=", useSecondValue, condition.distinct);
				}
				break;
			}
			case "SELF": {
				if (!on) {
					console.error("For SELF conditions you must provide an 'on' property");
					break;
				}

				// Basic evaluation condition
				const firstValue = getValueOnProperty(on, first);

				// Resolvers
				if (resolveFn) {
					evaluation = resolveFn(firstValue);
				}

				if ("distinct" in condition) {
					const useValue = firstValue || 0;
					evaluation = compare("!=", useValue, condition.distinct);
				}
				break;
			}
			case "ANY": {
				if (!Array.isArray(first)) {
					console.error("For ANY conditions you must provide a list");
					break;
				}

				const listValues = first.map((item) => {
					const value = getValueOnProperty(on, item.element);
					return toFloatValue(value);
				});

				if (listValues.length && "distinct" in condition) {
					evaluation = listValues.some((value) => value !== condition.distinct);
				}

				break;
			}
			default:
				break;
		}

		switch (condition?.logic || "") {
			case "not":
				return !Boolean(evaluation);
			default:
				break;
		}
	}

	return evaluation;
};

const evaluateOperation = (operation, element, on, when = null, total = 0) => {
	if (Array.isArray(element)) {
		element.forEach((item) => {
			total = evaluateOperation(operation, item, on, when, total);
		});
	}

	const useValue = getValueOnProperty(on, element) || 0;

	if (when) {
		const whenValue = getValueOnProperty(when.key, element);
		if (whenValue !== when.value) {
			return total;
		}
	}

	if (operation === "SUM") {
		total += useValue;
	}

	return total;
};

const isObject = (obj) => typeof obj === "object" && obj !== null;

const computeMatchesValue = (current = null, next = null, matches = 0) => {
	if (!current || !next) {
		return matches;
	}

	const currentValue = getValueOnProperty("value", current) || 0;
	const nextValue = getValueOnProperty("value", next) || 0;
	matches += currentValue === 0 && nextValue === 0;
	return matches;
};

const haveZeroes = (section = [], on = null, type = "SIMPLE") => {
	if (on) {
		let matches = 0;
		for (let i = 0; i < section.length; i++) {
			if (isObject(on) && type !== "DOUBLE_INPUT") {
				return matches;
			}

			if (type === "DOUBLE_INPUT") {
				const { from, to } = on;
				const current = section[i][to];
				const next = section?.[i + 1]?.[from];
				matches = computeMatchesValue(current, next, matches);
			} else {
				matches += (getValueOnProperty(`${on}.value`, section[i]) || 0) === 0;
			}
		}
		return matches;
	}
	return false;
};

const getTotalZeroes = (section = [], on = null) =>
	[...section].reduce((total, item) => {
		const value = (getValueOnProperty(`${on}.value`, item) || 0) === 0;
		return total + value;
	}, 0);

const adjacentZero = (errors = [], section = [], on = null) => {
	if (on) {
		const needsCheck = getTotalZeroes(section, on);
		if (needsCheck < 2) {
			return errors;
		}

		for (let index = 0; index < section.length; index++) {
			const currentItem = section[index];
			const nextItem = section?.[index + 1];

			if (currentItem && nextItem) {
				const currentValue = getValueOnProperty(`${on}.value`, currentItem) || 0;
				const nextValue = getValueOnProperty(`${on}.value`, nextItem) || 0;
				if (currentValue === 0 && nextValue === 0) {
					errors[index][on] = false;
					errors[index + 1][on] = false;
					break;
				}
			}
		}
	}
	return errors;
};

const checkZeroes = (errors = [], section = [], on = null) => {
	if (on) {
		const needsCheck = getTotalZeroes(section, on);
		if (!needsCheck) {
			return errors;
		}

		for (let index = 0; index < section.length; index++) {
			const currentItem = section[index];
			if (currentItem) {
				const currentValue = getValueOnProperty(`${on}.value`, currentItem) || 0;
				if (!currentValue) {
					if (!errors?.[index]?.[on]) {
						errors[index] = { [on]: false };
					}
					errors[index][on] = true;
				}
			}
		}
	}
	return errors;
};

const checkMatrixConstraints = (errors = [], section = [], constraints = []) => {
	if (constraints?.length) {
		const useConstraints = constraints?.filter((condition) => condition.apply);
		for (const condition of useConstraints) {
			switch (condition.constraint) {
				case "oneAdjacentZero":
					errors = adjacentZero(errors, section, condition.on);
					break;
				case "noZeroes":
					errors = checkZeroes(errors, section, condition.on);
					break;
				default:
					break;
			}
		}
	}
	return errors;
};

const evaluateMatrixCondition = (section = [], keys = [], condition) => {
	let isValid = true;
	let indexesErrors = [];

	if (section.length && keys.length && condition) {
		if (condition?.type === "STEP") {
			for (const onKey of keys) {
				for (let i = 0; i < section.length; i++) {
					const current = section[i]?.[onKey];
					const next = section?.[i + 1]?.[onKey] || null;
					if (current && next) {
						const valid = evaluateCondition(condition, null, next, current);
						// If valid -> false, then error TRUE, if valid -> true then error FALSE
						indexesErrors[i] = { ...(indexesErrors?.[i] || null), [onKey]: !valid };
						indexesErrors[i + 1] = { ...(indexesErrors?.[i + 1] || null), [onKey]: !valid };
					}
				}
			}
		}

		if ("constraints" in condition) {
			indexesErrors = checkMatrixConstraints(indexesErrors, section, condition.constraints);
		}

		isValid = isValidSection(indexesErrors);

		switch (condition?.logic) {
			case "not":
				return !Boolean(isValid);
			default:
				break;
		}
	}

	return {
		isValid,
		indexesErrors,
	};
};

const evaluateOperationCondition = (condition, row = null, columns = [], mapKeys = []) => {
	let evaluation = true;

	if (condition && row && mapKeys?.length) {
		const on = condition?.on || null;
		const operation = condition?.operation || null;
		const when = condition?.when || null;
		const limit = condition?.limit || 100000000;
		const operator = condition?.compare;
		// const constraints = condition?.constraints || null;

		if (!on) {
			console.error("For OPERATION conditions you must provide an 'on' property");
			return evaluation;
		}

		if (!isAllowedOperator(operator)) {
			console.error(`Invalid compare operator :::"${operator}"::: at evaluateOperationCondition on handleValidation errors`);
			return evaluation;
		}

		switch (condition.type) {
			case "ROW": {
				// Row attributes
				let rowTotal = 0;

				for (const index of mapKeys) {
					const current = row?.[index];
					if (current) {
						rowTotal = evaluateOperation(operation, current, on, when, rowTotal);
					}
				}

				const validLimit = compare(operator, rowTotal, limit);
				evaluation = Boolean(validLimit);
				break;
			}
			case "COLUMN": {
				// Column attributes
				let total = 0;
				for (const element of columns) {
					for (const index of mapKeys) {
						total = evaluateOperation(operation, element, index, when, total);
					}
				}
				const validLimit = compare(operator, total, limit);
				evaluation = Boolean(validLimit);
				break;
			}
			default:
				break;
		}
	}

	return evaluation;
};

const evaluateConditionalCondition = (condition, element, typeCondition = "SIMPLE") => {
	let evaluation = true;
	const apply = !Boolean("apply" in condition) || Boolean(condition?.apply);
	const on = condition?.on;

	if (!on) {
		console.error("For all CONDITIONAL conditions you must provide an 'on' property");
		return evaluation;
	}

	switch (typeCondition) {
		case "SIMPLE": {
			if (apply) {
				if ("excludes" in condition) {
					const { key, map, list } = condition.excludes;
					let distinct = condition.excludes?.distinct;
					let checkDistinct = element?.[key] !== distinct;

					let isApplicableCondition = !Boolean("distinct" in condition.excludes) || Boolean(checkDistinct);
					if (key && element && isApplicableCondition) {
						const evaluate = element?.[on];

						if (map.size || list) {
							let checkOnList = [];
							const checkWith = element?.[key];
							if (map.size && checkWith) {
								checkOnList = map.get(checkWith) || [];
							}
							if (list) {
								checkOnList = [...list];
							}

							if (checkOnList?.length) {
								let included = checkOnList?.includes(evaluate);
								evaluation = !Boolean(included);
							}
						}
					}
				}

				// if ("includes" in condition) {
				// 	const { key, map, list } = condition.includes;
				// 	// TODO: Implement includes condition when need it
				// }
			}
			break;
		}
		default:
			break;
	}

	if (condition?.logic === "not") {
		return !evaluation;
	}

	return evaluation;
};

const addErrorOnIndex = (index, key, errors = []) => {
	if (index !== -1 && Boolean(errors?.[index])) {
		errors[index][key] = true;
	}
	return errors;
};

const specialSelfValidation = (useKeys = null, indexes = null, applyOn = [], avoidOn = [], useErrors = []) => {
	if (useKeys && indexes && Boolean(applyOn?.length)) {
		const { from, to } = useKeys;
		applyOn.forEach((item) => {
			// Affect from
			if (item === from && !avoidOn.includes("from")) {
				addErrorOnIndex(indexes["from"], item, useErrors);
			}
			// Affect to
			if (item === to && !avoidOn.includes("to")) {
				addErrorOnIndex(indexes["to"], item, useErrors);
			}
		});
	}
	return useErrors;
};

// Inversion of elements and indexes
const getRightElement = (section, key, area, indexes, typeCondition = null, isSelfSetLocated = false) => {
	let element = section?.[key] || null;
	// Correct element based on type
	switch (typeCondition) {
		case "STEP":
			element = section?.[key + 1] || null;
			indexes.from = key + 1;
			break;
		default:
			indexes.from = key;
			break;
	}
	return Boolean(isSelfSetLocated) ? element : element?.[area] || null;
};

// Input Validations
const validateInputs = (section = [], conditions = [], localeIndexes = null, selfModifiers = null, useErrors = []) => {
	if (section.length && conditions.length && localeIndexes) {
		section.forEach((element, key) => {
			const indexes = { from: key, to: key };
			for (const condition of conditions) {
				const isSelfSetLocated = Boolean(condition.type === "SELF" && condition?.located === "SET");
				let first = getRightElement(section, key, localeIndexes.from, indexes, condition.type, isSelfSetLocated);
				let second = element?.[localeIndexes.to];

				const valid = evaluateCondition(condition, null, first, second);

				// Self special case
				if (!valid && condition.type === "SELF") {
					const applyOn = condition?.applyOn || selfModifiers?.applyOn || [];
					const avoidOn = condition?.avoidOn || selfModifiers?.avoidOn || [];
					specialSelfValidation({ from: localeIndexes.from, to: localeIndexes.to }, indexes, applyOn, avoidOn, useErrors);
					continue;
				}

				if (!valid && first) {
					useErrors[indexes.from][localeIndexes.from] = true;
					useErrors[indexes.to][localeIndexes.to] = true;
				}
			}
		});
	}
	return useErrors || [];
};

// Validations (Simple validations)
const validateDoubleInput = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		let useErrors = getMapErrors(section);

		// Operators conditions
		const conditions = operators?.conditions || [];
		const evaluators = operators?.evaluateAfterConditions || {};

		// Get pair from - to keys
		const { from, to } = getFromToKeys(mapKeys);
		if (!(from in section[0]) || !(to in section[0])) {
			console.error(`Missing FROM or TO index on type DOUBLE_INPUT at handleValidation errors`);
			return useErrors;
		}

		if (conditions.length) {
			validateInputs(section, conditions, { from, to }, null, useErrors);
		}

		if ("mergeErrors" in evaluators) {
			return evaluators.mergeErrors(useErrors, section);
		}

		return useErrors;
	}
	return [];
};

const validateSimple = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		let useErrors = getMapErrors(section);

		// Operators conditions
		const conditions = operators?.conditions || [];

		// Unique identifier
		const index = getSimpleIndex(mapKeys);
		if (!index) {
			console.error(`Missing INDEX on type SIMPLE at handleValidation errors`);
			return useErrors;
		}

		if (conditions.length) {
			validateInputs(section, conditions, { from: index, to: index }, { applyOn: [index], avoidOn: ["to"] }, useErrors);
		}

		return useErrors;
	}
	return [];
};

const validateOperation = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		let useErrors = getMapErrors(section);

		// Operators conditions
		const conditions = operators?.conditions || [];

		if (conditions.length) {
			section.forEach((element, key) => {
				const row = element;
				let isValid = true;

				for (const condition of conditions) {
					const valid = evaluateOperationCondition(condition, row, section, mapKeys);
					if (!valid) {
						isValid = false;
						break;
					}
				}

				if (!isValid) {
					writeErrorsOnIndex(useErrors, key);
				}
			});
		}

		return useErrors;
	}
	return [];
};

const validateComparative = (section = [], operators = null) => {
	if (section?.length && operators) {
		let useErrors = getMapErrors(section);

		// Use comparators if was provided
		const modifiers = operators?.modifiers || null;
		const maxInvalidConditions = operators?.maxInvalidConditions || 0;

		for (let i = 0; i < section.length; i++) {
			for (let j = i + 1; j < section.length; j++) {
				if (operators?.conditions) {
					let invalidConditions = 0;

					for (const condition of operators.conditions) {
						const required = Boolean(condition?.required);
						const valid = evaluateCondition(condition, modifiers, section[i], section[j]);

						if (required && valid) {
							invalidConditions--;
							continue;
						}

						if (!valid) {
							invalidConditions++;
						}

						if (invalidConditions === maxInvalidConditions) {
							break;
						}
					}

					if (!!invalidConditions) {
						writeErrorsOnIndex(useErrors, i);
						writeErrorsOnIndex(useErrors, j);
					}
				}
			}
		}

		return useErrors;
	}
	return [];
};

const getParticularCondition = (conditions = [], key) => {
	return conditions?.find((condition) => condition?.on === key || condition?.on.includes(key));
};

const validateMultiple = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		let useErrors = getMapErrors(section);

		// Operators conditions
		const conditions = operators?.conditions || [];
		const modifiers = operators?.modifiers || [];

		if (conditions.length && mapKeys?.length) {
			section.forEach((element, key) => {
				for (const on of mapKeys) {
					const condition = getParticularCondition(conditions, on);
					if (condition) {
						let nextElement = section?.[key + 1] || null; // Next element for conditions who applies
						let isValid = evaluateCondition(condition, modifiers, element, nextElement);
						if (!isValid) {
							useErrors[key][on] = true;
						}
					}
				}
			});
		}

		return useErrors;
	}
	return [];
};

const combineErrors = (useErrors = [], errors = [], property) => {
	for (let i = 0; i < useErrors.length; i++) {
		let error = useErrors?.[i]?.[property] || errors?.[i]?.[property];
		useErrors[i][property] = Boolean(error);
	}
};

const groupConditions = (conditions = [], groupBy = "order") => {
	const result = conditions?.reduce((reduction, definition) => {
		const lookup = definition?.[groupBy] || null;
		if (lookup) {
			const reductions = reduction?.[lookup] || [];
			reductions.push(definition);
			reduction[lookup] = reductions;
		}
		return reduction;
	}, {});
	return Object.keys(result)?.length ? result : null;
};

const evaluateAll = (section = [], mapKeys = [], definitions = []) => {
	let useErrors = getMapErrors(section);
	if (section?.length && mapKeys?.length && definitions?.length) {
		section.forEach((element, key) => {
			for (const on of mapKeys) {
				let isValid = true;
				for (const condition of definitions) {
					isValid = evaluateCondition(condition, null, element[on]);
					if (!isValid) break;
				}
				useErrors[key][on] = !Boolean(isValid);
			}
		});
	}
	return useErrors;
};

const evaluateColumns = (section = [], mapKeys = [], definitions = []) => {
	let useErrors = getMapErrors(section);
	if (section?.length && mapKeys?.length && definitions?.length) {
		for (const on of mapKeys) {
			const column = mapSection(section, [on]);
			if (column.length) {
				let errors = validateSimple(column, [on], { conditions: definitions });
				combineErrors(useErrors, errors, on);
			}
		}
	}
	return useErrors;
};

const evaluateRows = (section = [], mapKeys = [], definitions = []) => {
	let useErrors = getMapErrors(section);
	if (section?.length && mapKeys?.length && definitions?.length) {
		section.forEach((element, key) => {
			const row = getRowAsList(element, mapKeys, key);
			let isValid = true;
			for (const condition of definitions) {
				isValid = evaluateCondition(condition, null, row);
				if (!isValid) {
					switch (condition.type) {
						case "ANY":
							useErrors = writeErrorsOnIndex(useErrors, key);
							break;
						default:
							break;
					}
					break;
				}
			}
		});
	}
	return useErrors;
};

const validateComposed = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		let accumulator = new Map();

		// Operators conditions
		const baseConditions = operators?.conditions || [];
		// const modifiers = operators?.modifiers || [];
		const evaluators = operators?.evaluateAfterConditions || null;

		if (baseConditions.length && mapKeys?.length) {
			const conditions = groupConditions(baseConditions);
			if (conditions) {
				for (const [order, definitions] of Object.entries(conditions)) {
					switch (order) {
						case "ALL":
							accumulator.set("ALL", evaluateAll(section, mapKeys, definitions));
							break;
						case "COLUMNS":
							accumulator.set("COLUMNS", evaluateColumns(section, mapKeys, definitions));
							break;
						case "ROWS":
							accumulator.set("ROWS", evaluateRows(section, mapKeys, definitions));
							break;
						default:
							break;
					}
				}

				if ("mergeErrors" in evaluators) {
					return evaluators.mergeErrors(accumulator, section);
				}
			}
		}
	}
	return [];
};

const validateCustom = (section = [], mapKeys = [], operators = null) => {
	if (!section?.length || !operators) {
		return [];
	}
	const { customFn = () => {}, params = null } = operators;
	return customFn({ ...params, section, mapKeys });
};

const concatErrorElements = (original, current) => {
	const keys = Object.keys(original);
	for (const key of keys) {
		if (key in current) {
			let value = current[key];
			original[key] = original[key] || value;
		}
	}
	return original;
};

const writeMatrixErrors = (useErrors = [], errors = [], index, direction = "ROW") => {
	switch (direction) {
		case "ROW":
			for (let i = 0; i < useErrors[index].length; i++) {
				const current = useErrors[index][i];
				const element = concatErrorElements(current, errors[i]);
				useErrors[index][i] = element;
			}
			break;
		case "COLUMN":
			useErrors.forEach((row, key) => {
				const current = row[index] || null;
				const element = concatErrorElements(current, errors[key]);
				useErrors[key][index] = element;
			});
			break;
		default:
			break;
	}
	return useErrors;
};

const evaluateMatrixRows = (section = [], mapKeys = [], errors = [], definitions = []) => {
	if (section?.length && mapKeys?.length && definitions?.length) {
		for (let index = 0; index < section.length; index++) {
			const row = section[index];
			for (const condition of definitions) {
				let evaluate = evaluateMatrixCondition(row, mapKeys, condition);
				const { isValid, indexesErrors } = evaluate;
				if (!isValid) {
					errors = writeMatrixErrors(errors, indexesErrors, index);
				}
			}
		}
	}
	return errors;
};

const getMatrixColumn = (section, index = 0) => {
	let column = [];
	section.forEach((row) => {
		let element = row?.[index] || null;
		column.push(element);
	});
	return column;
};

const evaluateMatrixColumns = (section = [], mapKeys = [], errors = [], definitions = []) => {
	if (section?.length && mapKeys?.length && definitions?.length) {
		for (let index = 0; index < section[0].length; index++) {
			const column = getMatrixColumn(section, index);
			for (const condition of definitions) {
				let evaluate = evaluateMatrixCondition(column, mapKeys, condition);
				const { isValid, indexesErrors } = evaluate;
				if (!isValid) {
					errors = writeMatrixErrors(errors, indexesErrors, index, "COLUMN");
				}
			}
		}
	}
	return errors;
};

const evaluateMatrixDiagonal = (section = [], mapKeys = [], errors = [], definitions = []) => {
	if (section?.length && mapKeys?.length && definitions?.length) {
		for (const condition of definitions) {
			const onCondition = condition?.on || null;

			const firstElement = section[0]?.[0];
			const lastElement = section.at(-1)?.at(-1);

			if (firstElement && lastElement) {
				for (const key of mapKeys) {
					const firstValue = getValueOnProperty(onCondition, firstElement[key]);
					const lastValue = getValueOnProperty(onCondition, lastElement[key]);

					const isValid = compare(condition?.compare, firstValue, lastValue);

					if (!isValid) {
						errors[0][0][key] = true;
						const lastElementError = errors.at(-1)?.at(-1);
						lastElementError[key] = true;
					}
				}
			}
		}
	}
	return errors;
};

const validateMatrix = (section = [], mapKeys = [], operators = null) => {
	if (section?.length && operators) {
		const accumulator = new Map();

		// Operators conditions
		const baseConditions = operators?.conditions || [];

		const evaluators = operators?.evaluateAfterConditions || {};

		const useErrors = getMatrixMapErrors(section);

		const conditions = groupConditions(baseConditions);

		if (!conditions || !mapKeys?.length) {
			return [];
		}

		for (const [order, definitions] of Object.entries(conditions)) {
			switch (order) {
				case "COLUMNS":
					accumulator.set("COLUMNS", evaluateMatrixColumns(section, mapKeys, useErrors, definitions));
					break;
				case "ROWS":
					accumulator.set("ROWS", evaluateMatrixRows(section, mapKeys, useErrors, definitions));
					break;
				case "DIAGONAL":
					accumulator.set("DIAGONAL", evaluateMatrixDiagonal(section, mapKeys, useErrors, definitions));
					break;
				case "ALL":
				default:
					break;
			}
		}

		if ("mergeErrors" in evaluators) {
			return evaluators.mergeErrors(accumulator, section);
		}

		return useErrors;
	}
	return [];
};

const validateConditional = (section = [], operators = null, type = "SIMPLE") => {
	if (!section?.length || !operators) {
		return [];
	}

	const useErrors = getMapErrors(section);

	// Operators conditions
	const conditions = operators?.conditions || [];

	if (!conditions?.length) {
		return useErrors;
	}

	// Unique identifier
	section.forEach((_, key) => {
		for (const condition of conditions) {
			const isSelfSetLocated = Boolean(condition.type === "SELF" && condition?.located === "SET");
			const errorOn = condition?.on || null;

			if (!errorOn) {
				console.error("For all CONDITIONAL conditions you must provide an 'on' property");
				break;
			}

			const checkElement = getRightElement(section, key, errorOn, { from: errorOn }, condition.type, isSelfSetLocated);
			const valid = evaluateConditionalCondition(condition, checkElement, type);

			// Self special case
			if (!valid && condition.type === "SELF") {
				useErrors[key][errorOn] = true;
				continue;
			}

			if (!valid) {
				// TODO: Implement regular case when needed.
			}
		}
	});

	return useErrors;
};

// Validations (Constraints)
const isValidConstraints = (constraints = null, firstValue = null, secondValue = null) => {
	if ("notCrossZero" in constraints) {
		return !Boolean(firstValue < 0 && secondValue > 0);
	}
	return true;
};

// Validation checker
// Post errors checks (validations over errors)
const isValidSection = (errors = []) => {
	if (!errors.length) return true;
	return errors.every((element) => {
		if (Array.isArray(element)) return isValidSection(element);
		return Object.keys(element).every((key) => {
			if (Array.isArray(element[key])) return element[key].every((value) => !value);
			return !element[key];
		});
	});
};

const isValidAtLeastOneValue = (errors = []) => {
	if (!errors.length) return true;
	return errors.some((element) => {
		return Object.keys(element).some((key) => {
			if (Array.isArray(element[key])) return element[key].some((value) => !value);
			return !element[key];
		});
	});
};

const getErrors = (section = [], mapKeys = [], type, operators) => {
	let errors = [];

	if (section.length && type) {
		switch (type) {
			case "DOUBLE_INPUT":
				errors = validateDoubleInput(section, mapKeys, operators);
				break;
			case "SIMPLE":
				errors = validateSimple(section, mapKeys, operators);
				break;
			case "SIMPLE_CONDITIONAL":
				errors = validateConditional(section, operators);
				break;
			case "MULTIPLE":
				errors = validateMultiple(section, mapKeys, operators);
				break;
			case "OPERATION":
				errors = validateOperation(section, mapKeys, operators);
				break;
			case "COMPARATIVE":
				errors = validateComparative(section, operators);
				break;
			case "COMPOSED":
				errors = validateComposed(section, mapKeys, operators);
				break;
			case "CUSTOM":
				errors = validateCustom(section, mapKeys, operators);
				break;
			case "MATRIX":
				errors = validateMatrix(section, mapKeys, operators);
				break;
			default:
				break;
		}
	}

	return errors;
};

const detachValidate = (validations = [], mapErrors = new Map()) => {
	// Loop over validations
	validations.forEach((validation) => {
		// Defaults
		const { definition, section } = validation;
		const { useKey, errorsMap, errorsMapOn, validationType, operators, specialProperties } = definition;

		// Process to map sections and work with specific attributes
		const mappedSection = mapSection(section, errorsMap, errorsMapOn);

		// Get section errors (validations)
		const validationErrors = getErrors(mappedSection, errorsMap, validationType, operators);

		// Check special properties (check special cases)
		const errors = checkSpecialProperties(validationErrors, mappedSection, errorsMap, validationType, specialProperties);

		// Validate
		const isValid = isValidSection(errors);

		mapErrors.set(useKey, { definition, errors: isValid ? [] : errors, isValid });
	});
	return mapErrors;
};

const handleValidations = (state, action) => {
	const { type } = action;
	switch (type) {
		case "CLEAR":
			return new Map();
		case "VALIDATE": {
			const { validations } = action;
			let mapErrors = new Map(state);
			return detachValidate(validations, mapErrors);
		}
		default:
			break;
	}
	return state;
};

const isValidMapErrors = (errors = new Map()) => {
	return !errors.size || [...errors].every(([key, error]) => key && error.isValid);
};

export { detachValidate, handleValidations, isValidMapErrors };
